import React, { useState, useEffect, useMemo } from 'react';
import { ConfirmCloseDialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/ConfirmCloseDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProjectNotesTab } from '@/components/crm/ProjectNotesTab';
import { ProjectInspectionsTab } from '@/components/crm/ProjectInspectionsTab';
import ProfilesCombobox from '@/components/crm/ProfilesCombobox';
import { SharePointAuthButton } from '@/components/crm/SharePointAuthButton';
import { SharePointBrowser } from '@/components/crm/SharePointBrowser';
import { useSharePointAuth } from '@/hooks/useSharePointAuth';
import { useProfiles } from '@/hooks/useProfiles';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { PROJECT_STAGES, stageConfig } from '@/components/crm/project/projectUtils';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNavigate } from 'react-router-dom';
import { Calculator } from 'lucide-react';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';

interface Organization {
  id: string;
  name: string;
}

interface ProjectTemplate {
  id: string;
  name: string;
  schema: any;
}

interface Project {
  id: string;
  name: string;
  stage: string;
  org_id?: string;
  template_id?: string;
  template_snapshot?: any;
  amount?: number;
  probability?: number;
  close_date?: string;
  type?: string;
  project_number?: string;
  data?: any;
  owner_id?: string;
}

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  project?: Project | null;
  onSaved: () => void;
  templates?: ProjectTemplate[];
}

export const ProjectModal: React.FC<ProjectModalProps> = ({ isOpen, onClose, project, onSaved, templates }) => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { profiles } = useProfiles();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isConnected: isSharePointConnected } = useSharePointAuth();
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [fetchedTemplates, setFetchedTemplates] = useState<ProjectTemplate[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    stage: 'verkennen',
    org_id: '',
    template_id: '',
    amount: '',
    probability: '',
    close_date: '',
    type: '',
    project_number: '',
    owner_id: '',
    sharepoint_main_url: ''
  });
  const [templateSchema, setTemplateSchema] = useState<{ sections: any[] } | null>(null);
  const [customData, setCustomData] = useState<Record<string, any>>({});
  const [sharepointLinks, setSharepointLinks] = useState<any[]>([]);
  const [projectImages, setProjectImages] = useState<any[]>([]);

  // Track unsaved changes - combine formData and customData
  const formState = useMemo(() => ({ ...formData, customData }), [formData, customData]);
  const { hasUnsavedChanges, resetInitialValues } = useUnsavedChanges(formState, isOpen);

  useEffect(() => {
    const fetchData = async () => {
      const [orgsResult, templatesResult] = await Promise.all([
        supabase.from('organizations').select('id, name').is('deleted_at', null).order('name'),
        supabase.from('project_templates').select('id, name, schema').order('name')
      ]);
      
      if (orgsResult.error) {
        console.error('Error fetching organizations:', orgsResult.error);
      } else {
        setOrganizations(orgsResult.data || []);
      }
      
      if (templatesResult.error) {
        console.error('Error fetching templates:', templatesResult.error);
      } else {
        setFetchedTemplates(templatesResult.data || []);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        stage: project.stage || 'verkennen',
        org_id: project.org_id || '',
        template_id: project.template_id || '',
        amount: project.amount?.toString() || '',
        probability: project.probability?.toString() || '',
        close_date: project.close_date || '',
        type: project.type || '',
        project_number: project.project_number || '',
        owner_id: project.owner_id || '',
        sharepoint_main_url: (project as any).sharepoint_main_url || ''
      });
      
      // Always use current template schema instead of snapshot for existing projects
      if (project.template_id) {
        const allTemplates = templates || fetchedTemplates;
        const template = allTemplates.find(t => t.id === project.template_id);
        if (template && template.schema) {
          setTemplateSchema(template.schema);
          setCustomData(project.data || {});
        }
      } else if (project.template_snapshot && project.template_snapshot.sections) {
        // Only fallback to snapshot if no template_id is present
        setTemplateSchema(project.template_snapshot);
        setCustomData(project.data || {});
      }
    } else {
      setFormData({
        name: '',
        stage: 'verkennen',
        org_id: '',
        template_id: '',
        amount: '',
        probability: '',
        close_date: '',
        type: '',
        project_number: '',
        owner_id: '',
        sharepoint_main_url: ''
      });
      setTemplateSchema(null);
      setCustomData({});
    }
  }, [project, fetchedTemplates, templates]);

  // Handle template selection change
  const handleTemplateChange = (templateId: string) => {
    setFormData(prev => ({ ...prev, template_id: templateId }));
    
    if (templateId && templateId !== 'none' && templateId !== '') {
      const allTemplates = templates || fetchedTemplates;
      const selectedTemplate = allTemplates.find(t => t.id === templateId);
      if (selectedTemplate && selectedTemplate.schema) {
        setTemplateSchema(selectedTemplate.schema);
        
        // Initialize new custom data with default values for new template
        const newCustomData: Record<string, any> = {};
        if (selectedTemplate.schema.sections) {
          selectedTemplate.schema.sections.forEach((section: any) => {
            section.fields?.forEach((field: any) => {
              newCustomData[field.id] = field.type === 'checkbox' ? false : '';
            });
          });
        }
        setCustomData(newCustomData);
      }
    } else {
      // Clear template schema and custom data when "Geen template" is selected
      setTemplateSchema(null);
      setCustomData({});
    }
  };

  const handleCustomDataChange = (fieldId: string, value: any) => {
    setCustomData(prev => ({ ...prev, [fieldId]: value }));
  };

  const renderTemplateField = (field: any) => {
    const value = customData[field.id] || '';
    
    switch (field.type) {
      case 'text':
        return (
          <Input
            value={value}
            onChange={(e) => handleCustomDataChange(field.id, e.target.value)}
            placeholder={field.label}
          />
        );
      case 'textarea':
        return (
          <Textarea
            value={value}
            onChange={(e) => handleCustomDataChange(field.id, e.target.value)}
            placeholder={field.label}
          />
        );
      case 'number':
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => handleCustomDataChange(field.id, e.target.value)}
            placeholder={field.label}
          />
        );
      case 'date':
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => handleCustomDataChange(field.id, e.target.value)}
          />
        );
      case 'checkbox':
        return (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={field.id}
              checked={value || false}
              onCheckedChange={(checked) => handleCustomDataChange(field.id, checked)}
            />
            <Label htmlFor={field.id}>{field.label}</Label>
          </div>
        );
      case 'select':
        return (
          <Select value={value} onValueChange={(val) => handleCustomDataChange(field.id, val)}>
            <SelectTrigger className="z-[10020]">
              <SelectValue placeholder={`Selecteer ${field.label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent className="z-[10020]">
              {field.options?.map((option: string) => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      default:
        return (
          <Input
            value={value}
            onChange={(e) => handleCustomDataChange(field.id, e.target.value)}
            placeholder={field.label}
          />
        );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: 'Fout',
        description: 'Je moet ingelogd zijn om een project op te slaan.',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    
    try {
      // Validate project number uniqueness
      const projectNumber = formData.project_number?.trim();
      if (projectNumber) {
        // Validate length
        if (projectNumber.length > 50) {
          toast({
            title: "Projectnummer te lang",
            description: "Projectnummer mag maximaal 50 tekens zijn",
            variant: "destructive"
          });
          setLoading(false);
          return;
        }

        // Use count query for better reliability
        let query = supabase
          .from('projects')
          .select('id', { count: 'exact', head: true })
          .eq('project_number', projectNumber);
        
        // Only exclude current project if editing (not creating new)
        if (project?.id) {
          query = query.neq('id', project.id);
        }
        
        const { count, error: checkError } = await query;

        if (checkError) {
          console.warn('Projectnummer check warning:', checkError);
          toast({
            title: "Let op",
            description: "Kon projectnummer niet controleren, maar wordt wel opgeslagen",
            variant: "default"
          });
          // Don't block creation on technical errors
        } else if ((count ?? 0) > 0) {
          toast({
            title: "Projectnummer bestaat al",
            description: "Dit projectnummer is al in gebruik",
            variant: "destructive"
          });
          setLoading(false);
          return;
        }

        // Update formData with trimmed value
        formData.project_number = projectNumber;
      }

      const projectData = {
        name: formData.name,
        stage: formData.stage,
        org_id: formData.org_id === 'none' ? null : formData.org_id || null,
        template_id: formData.template_id === 'none' ? null : formData.template_id || null,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        probability: formData.probability ? parseInt(formData.probability) : null,
        close_date: formData.close_date || null,
        type: formData.type || null,
        project_number: formData.project_number || null,
        owner_id: formData.owner_id || user.id,
        data: customData
      };

      if (project) {
        // Update template_snapshot to current template when saving existing project
        const updateData: any = { ...projectData };
        if (templateSchema) {
          updateData.template_snapshot = templateSchema;
        }

        const { error } = await supabase
          .from('projects')
          .update(updateData)
          .eq('id', project.id);

        if (error) throw error;

        toast({
          title: 'Project bijgewerkt',
          description: 'Het project is succesvol bijgewerkt.',
        });
      } else {
        const templateSnapshot = templateSchema || {};
        
        const { data: newProject, error } = await supabase
          .from('projects')
          .insert({
            ...projectData,
            template_snapshot: templateSnapshot
          })
          .select()
          .single();

        if (error) throw error;

        // Create SharePoint folder for new project
        try {
          const { data: folderData, error: folderError } = await supabase.functions.invoke(
            'sharepoint-create-folder', 
            {
              body: {
                projectId: newProject.id,
                projectNumber: formData.project_number,
                projectName: formData.name
              }
            }
          );
          
          if (folderError) {
            console.error('SharePoint folder creation failed:', folderError);
            toast({
              title: "Project aangemaakt",
              description: "Project is aangemaakt. SharePoint map kan later worden aangemaakt.",
            });
          } else {
            toast({
              title: "Project en SharePoint map aangemaakt",
              description: `Project en map "${folderData.folderPath}" zijn aangemaakt.`,
            });
          }
        } catch (folderError: any) {
          console.error('SharePoint folder creation error:', folderError);
          toast({
            title: "Project aangemaakt",
            description: "Project is aangemaakt. SharePoint map kan later worden aangemaakt.",
          });
        }
      }

      resetInitialValues();
      onSaved();
      onClose();
    } catch (error: any) {
      console.error('Error saving project:', error);
      toast({
        title: 'Fout bij opslaan',
        description: error.message || 'Er ging iets mis bij het opslaan van het project.',
        variant: 'destructive'
      });
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!project) return;
    
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', project.id);

      if (error) throw error;

      toast({
        title: 'Project verwijderd',
        description: 'Het project is succesvol verwijderd.',
      });

      onClose();
      onSaved?.();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast({
        title: 'Fout bij verwijderen project',
        description: 'Er ging iets mis bij het verwijderen van het project.',
        variant: 'destructive'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <ConfirmCloseDialog
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      hasUnsavedChanges={hasUnsavedChanges}
      onConfirmDiscard={onClose}
      onConfirmSave={async () => {
        // Create a fake form event to trigger submit
        const form = document.querySelector('form');
        if (form) {
          form.requestSubmit();
        }
      }}
      isSaving={loading}
    >
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {project ? `Project bewerken ${project.project_number ? `- ${project.project_number}` : ''}` : 'Nieuw project'}
          </DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className={isMobile ? "flex flex-col w-full h-auto" : "grid w-full grid-cols-4"}>
            <TabsTrigger value="general" className={isMobile ? "w-full justify-start" : ""}>Algemeen</TabsTrigger>
            <TabsTrigger value="management" disabled={!project} className={isMobile ? "w-full justify-start" : ""}>Project Management</TabsTrigger>
            <TabsTrigger value="sharepoint" disabled={!project} className={isMobile ? "w-full justify-start" : ""}>SharePoint</TabsTrigger>
            <TabsTrigger value="inspections" disabled={!project} className={isMobile ? "w-full justify-start" : ""}>Inspecties</TabsTrigger>
          </TabsList>

          <TabsContent value="general">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="project_number">Projectnummer</Label>
                <Input
                  id="project_number"
                  value={formData.project_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, project_number: e.target.value }))}
                  placeholder="b.v. P2025001"
                />
              </div>

              <div>
                <Label htmlFor="name">Projectnaam *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="stage">Stadium</Label>
              <Select value={formData.stage} onValueChange={(value) => setFormData(prev => ({ ...prev, stage: value }))}>
                <SelectTrigger className="z-[10020]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[10020]">
                  {PROJECT_STAGES.map((stage) => (
                    <SelectItem key={stage} value={stage}>
                      {stageConfig[stage].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="type">Type</Label>
              <Input
                id="type"
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                placeholder="b.v. Zonnepanelen, Warmtepomp"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="org_id">Organisatie</Label>
            <Select value={formData.org_id} onValueChange={(value) => setFormData(prev => ({ ...prev, org_id: value }))}>
              <SelectTrigger className="z-[10020]">
                <SelectValue placeholder="Selecteer een organisatie" />
              </SelectTrigger>
              <SelectContent className="z-[10020]">
                <SelectItem value="none">Geen organisatie</SelectItem>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="template_id">Template</Label>
            <Select value={formData.template_id} onValueChange={handleTemplateChange}>
              <SelectTrigger className="z-[10020]">
                <SelectValue placeholder="Selecteer een template" />
              </SelectTrigger>
              <SelectContent className="z-[10020]">
                <SelectItem value="none">Geen template</SelectItem>
                {(templates || fetchedTemplates).map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="amount">Waarde (€)</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="probability">Kans (%)</Label>
              <Input
                id="probability"
                type="number"
                min="0"
                max="100"
                value={formData.probability}
                onChange={(e) => setFormData(prev => ({ ...prev, probability: e.target.value }))}
                placeholder="50"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="close_date">Verwachte sluitdatum</Label>
            <Input
              id="close_date"
              type="date"
              value={formData.close_date}
              onChange={(e) => setFormData(prev => ({ ...prev, close_date: e.target.value }))}
            />
          </div>

          <div>
            <Label htmlFor="owner_id">Eigenaar</Label>
            <ProfilesCombobox
              profiles={profiles}
              value={formData.owner_id}
              onValueChange={(value) => setFormData(prev => ({ ...prev, owner_id: value || '' }))}
              placeholder="Selecteer een eigenaar..."
            />
          </div>

          <div>
            <Label htmlFor="sharepoint_main_url">SharePoint URL</Label>
            <Input
              id="sharepoint_main_url"
              type="url"
              placeholder="https://yourdomain.sharepoint.com/..."
              value={formData.sharepoint_main_url}
              onChange={(e) => setFormData(prev => ({ ...prev, sharepoint_main_url: e.target.value }))}
            />
          </div>

          {/* Template Fields */}
          {templateSchema && templateSchema.sections && templateSchema.sections.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-lg font-medium">Projectgegevens</h4>
              {templateSchema.sections.map((section: any, sectionIndex: number) => (
                <div key={sectionIndex} className="space-y-3">
                  {section.title && (
                    <h5 className="text-md font-medium text-muted-foreground">{section.title}</h5>
                  )}
                  {section.fields?.map((field: any) => (
                    <div key={field.id} className="space-y-2">
                      {field.type !== 'checkbox' && (
                        <Label htmlFor={field.id}>{field.label}</Label>
                      )}
                      {renderTemplateField(field)}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

              <div className="flex justify-between items-center">
                {project && (
                  <div className="flex gap-2">
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => {
                        navigate(`/crm/projects/${project.id}/businesscase`);
                        onClose();
                      }}
                    >
                      <Calculator className="h-4 w-4 mr-2" />
                      Businesscase
                    </Button>
                    
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button type="button" variant="destructive">
                          Verwijderen
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Project verwijderen</AlertDialogTitle>
                          <AlertDialogDescription>
                            Weet je zeker dat je dit project wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuleren</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={handleDelete}
                            disabled={isDeleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {isDeleting ? 'Verwijderen...' : 'Verwijderen'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
                
                <div className="flex gap-2 ml-auto">
                  <Button type="button" variant="outline" onClick={onClose}>
                    Annuleren
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Opslaan...' : project ? 'Bijwerken' : 'Toevoegen'}
                  </Button>
                </div>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="management">
            {project && <ProjectNotesTab projectId={project.id!} />}
          </TabsContent>
          
          <TabsContent value="sharepoint">
            {!project ? (
              <div className="p-4 text-center text-muted-foreground">
                Sla eerst het project op om SharePoint te gebruiken
              </div>
            ) : !isSharePointConnected ? (
              <div className="p-4">
                <SharePointAuthButton onConnected={() => {}} />
              </div>
            ) : (
              <div className="p-4">
                <SharePointBrowser 
                  projectId={project.id}
                  folderPath={(project as any).sharepoint_folder_path || `/Projecten/${project.project_number ? `${project.project_number} - ${project.name}` : project.name}`}
                />
              </div>
            )}
          </TabsContent>

          <TabsContent value="inspections">
            {project && (
              <ProjectInspectionsTab 
                projectId={project.id} 
                projectName={project.name}
                tenantId={project.org_id || null}
              />
            )}
          </TabsContent>

        </Tabs>
      </DialogContent>
    </ConfirmCloseDialog>
  );
};
